import React from "react";
import { Quote, MapPin } from "lucide-react";
import { resolveImage } from "../lib/api";

const About = ({ about, site }) => {
  if (!about) return null;
  const paragraphs = about.paragraphs || [];
  const process = about.process || [];
  const voxel = site?.voxel_assets || {};
  const image = about.designer_image || voxel.about;
  const imageOnLeft = about.image_position !== "right";

  const IllustrationBlock = (
    <div className="md:col-span-5 flex justify-center">
      <div className="relative w-full max-w-[520px] flex items-center justify-center min-h-[380px] md:min-h-[520px]">
        {image && (
          <img
            data-testid="about-voxel"
            src={resolveImage(image)}
            alt={`${site?.name || "Designer"} illustration`}
            className="w-full h-auto mk-drift"
          />
        )}
      </div>
    </div>
  );

  const TextBlock = (
    <div className="md:col-span-7">
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-[color:var(--card)] border border-[color:var(--card-border)]">
        <span className="w-2 h-2 rounded-full bg-[color:var(--violet)]" />
        {about.section_label || "About"}
      </div>
      <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold leading-[1.05]">
        {about.heading_pre}{" "}
        <em className="italic text-[color:var(--violet)]">{about.heading_em}</em>
        {about.heading_post}
      </h2>
      {site?.location && (
        <div className="mt-3 inline-flex items-center gap-2 text-sm text-[color:var(--ink-soft)]">
          <MapPin className="w-4 h-4 text-[color:var(--violet)]" />
          Based in {site.location}
        </div>
      )}
      <div className="mt-6 space-y-4 text-[color:var(--ink-soft)] leading-relaxed">
        {paragraphs.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {about.quote && (
        <div className="mt-8 p-5 md:p-6 rounded-2xl bg-[color:var(--bg-2)] border border-[color:var(--card-border)] relative">
          <Quote className="w-6 h-6 text-[color:var(--violet)]" />
          <p className="mt-2 font-display italic text-xl md:text-2xl leading-snug">
            &ldquo;{about.quote}&rdquo;
          </p>
        </div>
      )}

      {process.length > 0 && (
        <div className="mt-10 grid sm:grid-cols-3 gap-4">
          {process.map((p, i) => (
            <div
              key={p.step + p.title + i}
              className="p-5 rounded-2xl bg-[color:var(--card)] border border-[color:var(--card-border)]"
            >
              <div className="text-xs font-mono text-[color:var(--violet)]">{p.step}</div>
              <div className="mt-2 font-display text-xl font-semibold">{p.title}</div>
              <p className="mt-1.5 text-sm text-[color:var(--ink-soft)]">{p.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <section id="about" className="relative py-20 md:py-28 bg-[color:var(--bg)] overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          {imageOnLeft ? (
            <>
              {IllustrationBlock}
              {TextBlock}
            </>
          ) : (
            <>
              {TextBlock}
              {IllustrationBlock}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default About;
